import adminRolesConfig from './admin-roles.json';

export type AdminPermissions = {
    canAccess: boolean;
    canRequestChange: boolean;
    canReview: boolean;
    canApprove: boolean;
    canApply: boolean;
};

export type AdminRoleDefinition = {
    id: string;
    label: string;
    aliases?: string[];
    permissions: AdminPermissions;
};

const noAccess: AdminPermissions = {
    canAccess: false,
    canRequestChange: false,
    canReview: false,
    canApprove: false,
    canApply: false,
};

export const adminRoles: AdminRoleDefinition[] = adminRolesConfig.roles;

export function getAdminAccessRoleIds(): string[] {
    return adminRoles
        .filter(role => role.permissions.canAccess)
        .flatMap(role => [role.id, ...(role.aliases ?? [])]);
}

export function collectAssignedRoles(contextRoles: string[] | undefined, tokenRoles: unknown): string[] {
    const fromContext = contextRoles ?? [];
    const fromToken = Array.isArray(tokenRoles)
        ? tokenRoles.filter((role): role is string => typeof role === 'string')
        : [];
    return [...new Set([...fromContext, ...fromToken].map(role => role.trim()).filter(Boolean))];
}

export function resolveAdminPermissions(assignedRoles: string[]): AdminPermissions {
    const normalized = new Set(assignedRoles.map(role => role.toLowerCase()));
    const matched = adminRoles.filter(role =>
        normalized.has(role.id.toLowerCase())
        || (role.aliases ?? []).some(alias => normalized.has(alias.toLowerCase())));
    if (matched.length === 0) return { ...noAccess };

    return matched.reduce<AdminPermissions>((acc, role) => ({
        canAccess: acc.canAccess || role.permissions.canAccess,
        canRequestChange: acc.canRequestChange || role.permissions.canRequestChange,
        canReview: acc.canReview || role.permissions.canReview,
        canApprove: acc.canApprove || role.permissions.canApprove,
        canApply: acc.canApply || role.permissions.canApply,
    }), { ...noAccess });
}
